import { useState, lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Image } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PLANNING_STATUSES,
  PLANNED_FORMATS,
  PRIORITIES,
  VIGENCIA_OPTIONS,
  type PlanningItem,
  type PlanningStatus,
  type PlannedFormat,
  type Priority,
  type Vigencia,
  useCreatePlanningItem,
  useUpdatePlanningItem,
} from '@/hooks/useContentPlanning';
import { toast } from 'sonner';
import { useContentCategories } from '@/hooks/useContentCategories';
import { VideoUploadSection } from '@/components/admin/VideoUploadSection';
import { FileUploadSection } from '@/components/admin/FileUploadSection';
import { ContentProgramsBadge } from '@/components/admin/content/ContentProgramsBadge';
import { ContentChapterAssignmentsTab } from '@/components/admin/planning/ContentChapterAssignmentsTab';


const RichTextEditor = lazy(() => import('@/components/RichTextEditor'));

const ACCESS_LEVELS = [
  { value: 'free', label: 'Gratis' },
  { value: 'subscription', label: 'Suscripción' },
  { value: 'program_only', label: 'Solo ruta' },
];

const CONTENT_TYPES = ['Video', 'Interactivo', 'Infografía', 'Clase con LIA', 'Recurso', 'Lectura'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: PlanningItem | null;
}

type FormState = Partial<PlanningItem> & {
  name: string;
  tagsInput: string;
};

const buildInitialForm = (item: PlanningItem | null): FormState => ({
  name: item?.name ?? '',
  description: item?.description ?? '',
  category: item?.category ?? '',
  content_type: item?.content_type ?? '',
  planning_status: item?.planning_status ?? 'suggested',
  responsible: item?.responsible ?? '',
  planned_format: item?.planned_format ?? null,
  priority: item?.priority ?? 'media',
  prospect_route: item?.prospect_route ?? '',
  vigencia: item?.vigencia ?? 'vigente',
  observations: item?.observations ?? '',
  duration: item?.duration ?? '',
  url: item?.url ?? '',
  access_level: item?.access_level ?? 'free',
  thumbnail_url: item?.thumbnail_url ?? '',
  author: item?.author ?? '',
  vimeo_id: item?.vimeo_id ?? '',
  vimeo_uri: item?.vimeo_uri ?? '',
  vimeo_player_url: item?.vimeo_player_url ?? '',
  html_content: item?.html_content ?? '',
  target_levels: item?.target_levels ?? [],
  sort_order: item?.sort_order ?? 0,
  tagsInput: (item?.tags ?? []).join(', '),
});

export function ContentPlanningDialog({ open, onOpenChange, item }: Props) {
  const isEdit = !!item;
  const createMut = useCreatePlanningItem();
  const updateMut = useUpdatePlanningItem();
  const { data: categories = [] } = useContentCategories();

  const [form, setForm] = useState<FormState>(() => buildInitialForm(item));

  // Reset when opening / item changes
  const itemKey = item?.id ?? 'new';
  const [currentKey, setCurrentKey] = useState(itemKey);
  if (currentKey !== itemKey) {
    setCurrentKey(itemKey);
    setForm(buildInitialForm(item));
  }

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    // Auto-extract Vimeo metadata if URL pasted
    let vimeo_id = form.vimeo_id ?? '';
    let vimeo_uri = form.vimeo_uri ?? '';
    let vimeo_player_url = form.vimeo_player_url ?? '';
    if (form.content_type === 'Video' && !vimeo_id && form.url) {
      const m = form.url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/)?(\d+)/);
      if (m) {
        vimeo_id = m[1];
        vimeo_uri = `/videos/${m[1]}`;
        vimeo_player_url = `https://player.vimeo.com/video/${m[1]}`;
      }
    }

    const tags = form.tagsInput
      ? form.tagsInput.split(',').map((t) => t.trim()).filter(Boolean)
      : [];

    const payload: any = {
      name: form.name,
      description: form.description || null,
      category: form.category || null,
      content_type: form.content_type || null,
      planning_status: form.planning_status,
      responsible: form.responsible || null,
      planned_format: form.planned_format,
      priority: form.priority,
      prospect_route: form.prospect_route || null,
      vigencia: form.vigencia ?? 'vigente',
      observations: form.observations || null,
      duration: form.duration || null,
      url: form.url || null,
      tags: tags.length > 0 ? tags : null,
      access_level: form.access_level || 'free',
      thumbnail_url: form.thumbnail_url || null,
      author: form.author || null,
      vimeo_id: vimeo_id || null,
      vimeo_uri: vimeo_uri || null,
      vimeo_player_url: vimeo_player_url || null,
      html_content: form.html_content || null,
      target_levels: form.target_levels ?? [],
      sort_order: form.sort_order ?? 0,
    };

    try {
      if (isEdit && item) {
        await updateMut.mutateAsync({ id: item.id, ...payload });
        toast.success('Contenido actualizado');
      } else {
        await createMut.mutateAsync(payload);
        toast.success('Contenido planificado creado');
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al guardar');
    }
  };

  const isSaving = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar contenido' : 'Nuevo contenido planificado'}</DialogTitle>
        </DialogHeader>

        {isEdit && item?.id && (
          <div className="pt-2">
            <ContentProgramsBadge contentId={item.id} />
          </div>
        )}

        <div className="grid gap-2 py-2">
          <Label>Nombre *</Label>
          <Input
            value={form.name ?? ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ej: Introducción a Agentes IA"
          />
        </div>

        <Tabs defaultValue="planning" className="w-full">
          <TabsList className={`grid w-full ${isEdit && item?.id ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <TabsTrigger value="planning">Planificación</TabsTrigger>
            <TabsTrigger value="metadata">Metadatos</TabsTrigger>
            <TabsTrigger value="content">Contenido</TabsTrigger>
            {isEdit && item?.id && <TabsTrigger value="routes">Rutas</TabsTrigger>}
          </TabsList>


          {/* === Tab 1: Planning === */}
          <TabsContent value="planning" className="grid gap-4 pt-4">
            <div className="grid gap-2">
              <Label>Descripción</Label>
              <Textarea
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="¿De qué tratará este contenido?"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <Label>Estado</Label>
                <Select
                  value={form.planning_status ?? 'suggested'}
                  onValueChange={(v) => setForm({ ...form, planning_status: v as PlanningStatus })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLANNING_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Prioridad de Desarrollo</Label>
                <Select
                  value={form.priority ?? 'media'}
                  onValueChange={(v) => setForm({ ...form, priority: v as Priority })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Formato Planificado</Label>
              <Select
                value={form.planned_format ?? '__none__'}
                onValueChange={(v) => setForm({ ...form, planned_format: v === '__none__' ? null : (v as PlannedFormat) })}
              >
                <SelectTrigger><SelectValue placeholder="Selecciona un formato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin definir</SelectItem>
                  {PLANNED_FORMATS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <Label>Responsable de Desarrollo</Label>
                <Input
                  value={form.responsible ?? ''}
                  onChange={(e) => setForm({ ...form, responsible: e.target.value })}
                  placeholder="Nombre del responsable"
                />
              </div>
              <div className="grid gap-2">
                <Label>Categoría</Label>
                <Select
                  value={form.category && form.category.length > 0 ? form.category : '__none__'}
                  onValueChange={(v) => setForm({ ...form, category: v === '__none__' ? null : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Selecciona una categoría" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin categoría</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Ruta Prospecto</Label>
              <Input
                value={form.prospect_route ?? ''}
                onChange={(e) => setForm({ ...form, prospect_route: e.target.value })}
                placeholder="Ruta o programa donde se planea ubicar"
              />
            </div>

            <div className="grid gap-2">
              <Label>Vigencia</Label>
              <Select
                value={form.vigencia ?? 'vigente'}
                onValueChange={(v) => setForm({ ...form, vigencia: v as Vigencia })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VIGENCIA_OPTIONS.map((v) => (
                    <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>



            <div className="grid gap-2">
              <Label>Observaciones</Label>
              <Textarea
                value={form.observations ?? ''}
                onChange={(e) => setForm({ ...form, observations: e.target.value })}
                rows={3}
                placeholder="Notas internas del equipo de contenido"
              />
            </div>

            {form.planning_status === 'published' && (
              <div className="text-xs text-muted-foreground bg-muted/50 border rounded-md p-3">
                Recuerda: para que un contenido <strong>Publicado</strong> se muestre al usuario final,
                debe tener el contenido cargado (Vimeo, archivo, HTML, etc.) en la pestaña <strong>Contenido</strong>.
              </div>
            )}
          </TabsContent>

          {/* === Tab 2: Metadata === */}
          <TabsContent value="metadata" className="grid gap-4 pt-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <Label>Tipo de contenido</Label>
                <Select
                  value={form.content_type ?? '__none__'}
                  onValueChange={(v) => setForm({ ...form, content_type: v === '__none__' ? null : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Selecciona un tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin definir</SelectItem>
                    {CONTENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Duración</Label>
                <Input
                  value={form.duration ?? ''}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  placeholder="ej. 12 min"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <Label>Autor</Label>
                <Input
                  value={form.author ?? ''}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  placeholder="Nombre del autor"
                />
              </div>

              <div className="grid gap-2">
                <Label>Nivel de acceso</Label>
                <Select
                  value={form.access_level ?? 'free'}
                  onValueChange={(v) => setForm({ ...form, access_level: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCESS_LEVELS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Etiquetas (separadas por coma)</Label>
              <Input
                value={form.tagsInput ?? ''}
                onChange={(e) => setForm({ ...form, tagsInput: e.target.value })}
                placeholder="ej. ia, automatización, n8n"
              />
            </div>

            <div className="grid gap-2">
              <Label>Niveles objetivo</Label>
              <div className="flex flex-wrap gap-3">
                {[0, 1, 2, 3, 4, 5].map((lvl) => {
                  const checked = (form.target_levels ?? []).includes(lvl);
                  return (
                    <label key={lvl} className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) => {
                          const current = form.target_levels ?? [];
                          setForm({
                            ...form,
                            target_levels: c
                              ? [...current, lvl].sort((a, b) => a - b)
                              : current.filter((l) => l !== lvl),
                          });
                        }}
                      />
                      <span className="text-sm">Nivel {lvl}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Orden (sort_order)</Label>
              <Input
                type="number"
                min={0}
                value={form.sort_order ?? 0}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
          </TabsContent>

          {/* === Tab 3: Content === */}
          <TabsContent value="content" className="grid gap-4 pt-4">
            {!form.content_type && (
              <div className="text-sm text-muted-foreground bg-muted/50 border rounded-md p-4">
                Selecciona primero un <strong>Tipo de contenido</strong> en la pestaña Metadatos.
              </div>
            )}

            {form.content_type === 'Video' && (
              <VideoUploadSection
                onVideoReady={(result) => {
                  setForm((prev) => ({
                    ...prev,
                    vimeo_id: result.vimeo_id,
                    vimeo_uri: result.vimeo_uri,
                    vimeo_player_url: result.vimeo_player_url,
                    thumbnail_url: result.thumbnail_url || prev.thumbnail_url || '',
                    duration: result.duration || prev.duration || '',
                  }));
                }}
                initialVimeoId={form.vimeo_id ?? ''}
                initialPlayerUrl={form.vimeo_player_url ?? ''}
                initialThumbnailUrl={form.thumbnail_url ?? ''}
                onThumbnailRefreshed={(url) =>
                  url && setForm((prev) => ({ ...prev, thumbnail_url: url }))
                }
              />
            )}

            {form.content_type === 'Infografía' && (
              <>
                <FileUploadSection
                  accept="image/*"
                  label="Subir Infografía (imagen)"
                  currentUrl={form.thumbnail_url || null}
                  onFileUploaded={(url) => setForm((prev) => ({ ...prev, thumbnail_url: url }))}
                  onClear={() => setForm((prev) => ({ ...prev, thumbnail_url: '' }))}
                />
                <FileUploadSection
                  accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                  label="Audio explicativo (opcional)"
                  currentUrl={form.url || null}
                  onFileUploaded={(url) => setForm((prev) => ({ ...prev, url }))}
                  onClear={() => setForm((prev) => ({ ...prev, url: '' }))}
                />
              </>
            )}

            {form.content_type === 'Recurso' && (
              <FileUploadSection
                accept="*/*"
                label="Subir recurso (archivo)"
                currentUrl={form.url || null}
                onFileUploaded={(url) => setForm((prev) => ({ ...prev, url }))}
                onClear={() => setForm((prev) => ({ ...prev, url: '' }))}
                showResourceUrl
                resourceUrl={form.url ?? ''}
                onResourceUrlChange={(url) => setForm((prev) => ({ ...prev, url }))}
              />
            )}

            {form.content_type === 'Interactivo' && (
              <>
                <FileUploadSection
                  accept=".html,.htm,text/html"
                  allowHtml
                  label="Subir archivo HTML interactivo (OVA)"
                  currentUrl={form.url || null}
                  onFileUploaded={(url) => setForm((prev) => ({ ...prev, url }))}
                  onClear={() => setForm((prev) => ({ ...prev, url: '' }))}
                />
                <div className="grid gap-2">
                  <Label className="text-xs text-muted-foreground">O pega una URL externa</Label>
                  <Input
                    value={form.url ?? ''}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </>
            )}

            {form.content_type === 'Clase con LIA' && (
              <div className="grid gap-2">
                <Label>Prompt del asistente (pasos de la clase)</Label>
                <p className="text-xs text-muted-foreground">
                  Escribe cada paso de la clase en una línea numerada (1. ..., 2. ..., 3. ...). LIA guiará al participante paso a paso.
                </p>
                <Textarea
                  value={form.html_content ?? ''}
                  onChange={(e) => setForm({ ...form, html_content: e.target.value })}
                  placeholder={'1. Explica qué es la IA generativa...\n2. Presenta 3 casos de uso...\n3. Guía al participante a definir un mini-proyecto...'}
                  rows={8}
                />
              </div>
            )}

            {form.content_type === 'Lectura' && (
              <div className="grid gap-2">
                <Label>
                  <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Contenido de la lectura</span>
                </Label>
                <Suspense fallback={<div className="h-[200px] animate-pulse bg-muted rounded-md" />}>
                  <RichTextEditor
                    value={form.html_content ?? ''}
                    onChange={(html) => setForm((prev) => ({ ...prev, html_content: html }))}
                    placeholder="Escribe el contenido de la lectura..."
                  />
                </Suspense>
              </div>
            )}

            {/* Thumbnail for non-Infografía types */}
            {form.content_type && form.content_type !== 'Infografía' && (
              <div className="grid gap-2">
                <Label>
                  <span className="flex items-center gap-1"><Image className="h-3.5 w-3.5" /> Imagen de portada</span>
                </Label>
                {form.content_type !== 'Video' ? (
                  <FileUploadSection
                    accept="image/*"
                    label=""
                    currentUrl={form.thumbnail_url || null}
                    onFileUploaded={(url) => setForm((prev) => ({ ...prev, thumbnail_url: url }))}
                    onClear={() => setForm((prev) => ({ ...prev, thumbnail_url: '' }))}
                  />
                ) : (
                  <Input
                    value={form.thumbnail_url ?? ''}
                    onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                    placeholder="https://..."
                  />
                )}
              </div>
            )}
          </TabsContent>

          {/* === Tab 4: Rutas === */}
          {isEdit && item?.id && (
            <TabsContent value="routes" className="pt-4">
              <ContentChapterAssignmentsTab contentId={item.id} />
            </TabsContent>
          )}
        </Tabs>


        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
